import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, MessageCircle, FileText, Users, Lightbulb, ArrowLeft, Workflow, Mail, HelpCircle, AlertCircle } from 'lucide-react';
import { StepCard, InfoCard } from './SectionComponents';

const InterviewProcessSection = ({ onBack, onComplete, isCompleted, onNext, nextSectionTitle, hasNext }) => {

  const interviewStages = [
    {
      Icon: MessageCircle,
      title: '1. Initial Interview (30–45 mins)',
      description: 'Often with a founder or early team member. Covers your background, motivations and general fit. Might feel casual but take it seriously - show hunger, curiosity and professionalism.',
              keyPoints: [
          '"Why this company and role?"',
          '"Tell me about yourself"',
          '"What\'s a problem you\'ve solved with limited guidance?"',
        ],
      pointsTitle: 'Questions might include:'
    },
    {
      Icon: FileText,
      title: '2. Take-Home Task or Case Study',
      description: 'A practical challenge linked to the job. Assesses your ability to prioritise, structure ideas and communicate clearly.',
      keyPoints: [
        "You'll often present your work in a follow-up conversation",
        'Keep it concise, show your thinking and explain assumptions',
      ],
    },
    {
      Icon: Users,
      title: '3. Final Round Interview',
      description: 'Can be with the founder, line manager or wider team. Often focused on values, self-awareness and whether you\'d be great to work with.',
      keyPoints: [
        'May involve multiple formats (e.g. panel, culture fit chat, coffee catch-up)',
      ],
    },
  ];

  const etiquetteTips = [
    "Reply to startups within 24 hours to keep momentum - this shows reliability",
    "Follow up if a startup hasn't replied in 48–72 hours",
    'Use polite chaser emails (e.g. "Just checking in to find a time that works for our next call...")',
    "Always send a thank-you email after interviews",
    "Reiterate your interest, reference something from the chat and confirm next steps",
    "Reschedule respectfully - if needed, give at least 4 hours' notice",
    "Communicate with Jumpstart - let Bethan (bethan@jumpstart-uk.com) know if you've received an offer. For all other queries, email info@jumpstart-uk.com and we'll get back to you ASAP",
  ];

  const commonQuestions = [
    "Why this company and why now?",
    "Tell me about a time you solved something with limited information",
    "How do you prioritise when everything feels urgent?",
    "What motivates you?",
    "When have you taken initiative outside your job description?",
    "Tell me about a time you failed or received tough feedback",
  ];

  const startupCares = [
    { title: 'Adaptability', emoji: '🔄', description: 'Can you stay calm and solve problems when things change fast?' },
    { title: 'Ownership', emoji: '💪', description: 'Do you take responsibility without being chased?' },
    { title: 'Initiative', emoji: '🚀', description: 'Are you the kind of person who gets started rather than waiting to be told what to do?' },
    { title: 'Comfort with ambiguity', emoji: '🧭', description: 'Can you figure things out when the path isn\'t clear?' },
  ];

  return (
    <div className="bg-white min-h-screen">
      {/* Hero Section */}
      <div className="w-full py-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12"
        >
          <div className="text-center mb-16">
            <h1 className="text-6xl font-bold text-gray-900 mb-6">
              How the typical Generalist interview process works
            </h1>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              Whilst every startup is different, most follow a 3-stage structure.
            </p>
          </div>
          <div className="flex items-center justify-center space-x-6">
            <button
              onClick={onBack}
              className="inline-flex items-center px-8 py-4 bg-white text-gray-700 rounded-2xl font-semibold hover:bg-gray-50 transition-all duration-300 shadow-lg border border-gray-200"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back to Resources
            </button>
            <button
              onClick={onComplete}
              className={`inline-flex items-center px-8 py-4 rounded-2xl font-semibold transition-all duration-300 shadow-lg ${
                isCompleted
                  ? 'bg-[#02B491] text-white shadow-emerald-500/30'
                  : 'bg-[#02B491] text-white hover:bg-emerald-600 shadow-emerald-500/20'
              }`}
            >
              {isCompleted ? (
                <>
                  <CheckCircle className="w-5 h-5 mr-2" />
                  Completed
                </>
              ) : (
                'Mark as Complete'
              )}
            </button>
          </div>
        </motion.div>
      </div>

      {/* Content */}
      <div className="w-full pb-20 bg-white">
        <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 space-y-16">
          <StepCard title="The Normal 3-Stage Startup Interview Process">
            <div className="space-y-10">
              {interviewStages.map((stage, index) => (
                <div key={index} className="space-y-6">
                  <div className="space-y-4">
                    <h4 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                      {index === 0 && "🎯 "}{stage.title}
                    </h4>
                    <p className="text-gray-600 text-lg leading-relaxed">
                      {index === 0 ? (
                        <>
                          Often with a founder or early team member. Covers your background, motivations and general fit. 
                          Might feel casual but take it seriously - show <span className="font-bold text-gray-800">hunger, curiosity and professionalism</span>.
                        </>
                      ) : index === 1 ? (
                        <>
                          A practical challenge linked to the job. Assesses your ability to <span className="font-bold text-gray-800">prioritise, structure ideas and communicate clearly</span>.
                        </>
                      ) : (
                        <>
                          Can be with the founder, line manager or wider team. Often focused on <span className="font-bold text-gray-800">values, self-awareness and whether you'd be great to work with</span>.
                        </>
                      )}
                    </p>
                  </div>
                  {stage.keyPoints && (
                    <div className="bg-white rounded-2xl p-6 border border-gray-200 ml-6">
                      {stage.pointsTitle && <h5 className="font-semibold text-gray-900 mb-4 text-lg">{stage.pointsTitle}</h5>}
                      <div className="space-y-3">
                        {stage.keyPoints.map((point, i) => (
                          <div key={i} className="flex items-start space-x-3">
                            <CheckCircle className="w-5 h-5 text-[#02B491] mt-1 flex-shrink-0" />
                            <span className="text-gray-700 leading-relaxed">{point}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </StepCard>

          <StepCard title="General Interview Etiquette & Tips">
            <div className="space-y-6">
              <p className="text-gray-600 text-lg leading-relaxed mb-8">
                Professional communication and follow-up shows reliability and genuine interest:
              </p>
              <div className="grid gap-4">
                {etiquetteTips.map((tip, i) => (
                  <div key={i} className="flex items-start space-x-4 bg-white rounded-xl p-4 border border-blue-200">
                    <CheckCircle className="w-5 h-5 text-blue-600 mt-1 flex-shrink-0" />
                    <span className="text-gray-800 leading-relaxed">{tip}</span>
                  </div>
                ))}
              </div>
            </div>
          </StepCard>

          <StepCard title="Most Common Generalist Interview Questions">
            <div className="space-y-6">
              <p className="text-gray-600 text-lg leading-relaxed mb-8">
                Prepare thoughtful answers with specific examples for these frequently asked questions:
              </p>
              <div className="space-y-4">
                {commonQuestions.map((question, i) => (
                  <div key={i} className="flex items-start space-x-3">
                    <div className="w-1.5 h-1.5 bg-gray-400 rounded-full flex-shrink-0 mt-2"></div>
                    <span className="text-gray-700 leading-relaxed font-medium">{question}</span>
                  </div>
                ))}
              </div>
            </div>
          </StepCard>

          <StepCard title="What Early-Stage Startups Care About">
            <div className="space-y-8">
              <p className="text-gray-600 text-lg leading-relaxed">
                These qualities are often more important than years of experience:
              </p>
              <div className="grid gap-6 md:grid-cols-2">
                {startupCares.map((item, i) => (
                   <div key={i} className="bg-white rounded-2xl p-6 border border-gray-200 hover:shadow-lg hover:shadow-gray-900/5 transition-all duration-300">
                     <div>
                       <h5 className="font-bold text-gray-900 text-lg mb-3 flex items-center gap-2">
                         <span className="text-xl">{item.emoji}</span>
                         {item.title}
                       </h5>
                       <p className="text-gray-600 leading-relaxed">
                         {i === 0 && (
                           <>Can you <span className="font-semibold text-gray-800">stay calm and solve problems</span> when things change fast?</>
                         )}
                         {i === 1 && (
                           <>Do you <span className="font-semibold text-gray-800">take responsibility</span> without being chased?</>
                         )}
                         {i === 2 && (
                           <>Are you the kind of person who <span className="font-semibold text-gray-800">gets started</span> rather than waiting to be told what to do?</>
                         )}
                         {i === 3 && (
                           <>Can you <span className="font-semibold text-gray-800">figure things out</span> when the path isn't clear?</>
                         )}
                       </p>
                     </div>
                   </div>
                ))}
              </div>
            </div>
          </StepCard>

          <InfoCard
            icon={Lightbulb}
            title="Bonus Tip"
            description="Startups often make decisions based on who they trust to take action and figure things out. Don't just say you're adaptable — back up your claims with evidence."
            accent={true}
          />

          <InfoCard
            icon={AlertCircle}
            title="Not heard back from a startup you've politely followed up with already?"
            description="Email us at info@jumpstart-uk.com"
            type="warning"
          />
        </div>
        
        {hasNext && (
          <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 pt-12">
            <div className="text-center">
              <button
                onClick={onNext}
                className="inline-flex items-center px-8 py-4 bg-white border-2 border-[#02B491] text-[#02B491] rounded-2xl font-semibold hover:bg-[#02B491] hover:text-white transition-all duration-300 shadow-lg"
              >
                Continue to: {nextSectionTitle}
                <svg className="w-5 h-5 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default InterviewProcessSection; 